import React, { Fragment, useState, useEffect } from "react";
import { Transition } from "@headlessui/react";
import { useChatContext } from "../../../contexts/ChatContext";

/**
 * Componente MainFrame.
 *
 * @component
 *
 * @description
 * O componente `MainFrame` exibe uma mensagem de boas-vindas com uma animação de transição suave ao ser carregado.
 * A transição é controlada pelo estado `show`, que é ativado após um breve atraso usando o `useEffect`.
 *
 * O componente utiliza o `Transition` da biblioteca `@headlessui/react` para animar a exibição do conteúdo com
 * efeitos de fade in e fade out.
 *
 * @returns {JSX.Element} O componente que exibe uma mensagem centralizada com animação de transição.
 *
 * @example
 * return <MainFrame />;
 */
function MainFrame() {
  const { showDAGSolScreen, showAcordosScreen } = useChatContext();
  const [show, setShow] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setShow(true);
    }, 50);
  }, []);

  /**
   * Returns the appropriate welcome title based on the active module.
   * @returns {string} The welcome title text.
   */
  const getWelcomeTitle = () => {
    if (showAcordosScreen) {
      return "Olá, Como posso ajudar hoje?";
    }
    if (showDAGSolScreen) {
      return "Converse com os Guias de Boas Práticas da DAGSol";
    }
    return "Olá! Como posso ajudar com seus projetos hoje?";
  };

  /**
   * Returns the appropriate welcome subtitle based on the active module.
   * @returns {string} The welcome subtitle text.
   */
  const getWelcomeSubtitle = () => {
    if (showAcordosScreen) {
      return "Tire dúvidas sobre contratos, fornecedores, conectividade, anexos e aditivos da base de dados da RNP";
    }
    if (showDAGSolScreen) {
      return "Um assistente preparado para tirar suas dúvidas sobre os Guias de Boas Práticas da DAGSol";
    }
    return "Projetado para tirar todas as suas dúvidas sobre os projetos da DEO";
  };

  return (
    <Transition
      show={show}
      as={Fragment}
      enter="transform ease-out duration-300 transition"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition ease-in duration-300"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      className="transform-gpu"
    >
      <div className="flex z-10 justify-center items-center w-full h-full">
        <div className="absolute top-0 right-0 left-0 h-[50vh]"></div>
        <div className="flex z-20 flex-col justify-center items-center mx-auto space-x-6 w-full">
          <div className="flex flex-col pb-2 mb-4 text-center select-none">
            <p className="mb-2 text-2xl font-bold text-slate-700 dark:text-slate-200">
              {getWelcomeTitle()}
            </p>
            <p className="text-[#374151] dark:text-slate-300 text-sm font-medium">
              {getWelcomeSubtitle()}
            </p>
          </div>
        </div>
      </div>
    </Transition>
  );
}

export default MainFrame;
